import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileText, Landmark, Lock } from "lucide-react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { aTasaMicro, comoTasa } from "@/componentes/tesoreria/conversion"
import { monto } from "@/componentes/tesoreria/dinero"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * Una orden de pago —o uno de sus pagos— vista desde el enlace del bot.
 * **Sin sesión, sin menú y sin poder tocar nada.**
 *
 * ⚠ **No da acceso a ninguna instancia del sistema** (lo pidió el usuario el 07/10):
 * vive fuera del `AppShell`, no hay ni un enlace hacia otra pantalla y lo que se ve lo
 * decide una lista blanca del servidor (`OrdenPublica`). Si el enlace se filtra, lo
 * máximo que se lleva quien lo tenga es esta página.
 *
 * ⚠ **Los soportes se abren por su número dentro de la orden**, por la misma ruta
 * pública y con el mismo token: no hay un id que cambiar para llegar a otro archivo.
 *
 * ⚠ **El error es uno solo**: caducado, revocado, pago anulado o inventado.
 */

type Pago = {
  fecha_valor: string
  banco: string
  monto_debitado: string
  divisa_cuenta: string
  tasa_aplicada: string | null
  monto_equivalente: string
  comision: string
  monto_igtf: string
  referencia: string
  ejecutado_por: string | null
}

type Orden = {
  codigo: string
  estado: string
  proveedor: string
  concepto: string
  divisa: string
  monto_base: string
  monto_exento: string
  monto_iva: string
  monto_ret_iva: string
  monto_islr: string
  monto_otros_impuestos: string
  monto_total: string
  monto_neto: string
  numero_documento: string | null
  fecha_emision: string | null
  fecha_requerida: string | null
  redactada_por: string | null
  aprobada_por: string | null
  aprobada_en: string | null
  lineas: { descripcion: string; cantidad: string; monto: string }[]
  pagos: Pago[]
  es_pago: boolean
  soportes: { numero: number; tipo: string; nombre: string; mime: string }[]
}

const ESTADO: Record<string, string> = {
  BORRADOR: "Borrador",
  EN_APROBACION: "En aprobación",
  APROBADA: "Aprobada",
  EJECUTADA: "Pagada",
  ANULADA: "Anulada",
}

function tasa(texto: string): string {
  const micro = aTasaMicro(texto)
  return micro === null ? texto : comoTasa(micro)
}

/** Una cifra que vale cero no se enseña: un desglose lleno de ceros no se lee. */
const esCero = (t: string) => /^-?0+(\.0+)?$/.test(t.trim())

export function OrdenPublica() {
  const { token = "" } = useParams()
  const consulta = useQuery({
    queryKey: ["publico", "orden", token],
    queryFn: () => api.get<Orden>(`/api/v1/publico/ordenes/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado o haberse revocado. Pídele uno nuevo a quien te lo mandó.
        </p>
      </div>
    )
  }

  const o = consulta.data
  const desglose: [string, string][] = (
    [
      ["Base", o.monto_base],
      ["Exento", o.monto_exento],
      ["IVA", o.monto_iva],
      ["Otros impuestos", o.monto_otros_impuestos],
      ["Total factura", o.monto_total],
      ["Retención de IVA", o.monto_ret_iva],
      ["Retención de ISLR", o.monto_islr],
    ] as [string, string][]
  ).filter(([, v]) => !esCero(v))

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-8">
      <p
        className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
        style={{ borderColor: "var(--border)", color: "var(--fg-muted)" }}
      >
        <Lock size={12} aria-hidden={true} />
        Vista de sólo lectura. El enlace caduca a los siete días.
      </p>

      <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
        {o.es_pago ? `Pago de la orden ${o.codigo}` : `Orden de pago ${o.codigo}`}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{o.proveedor}</h1>
      <p className="mt-1 text-sm">{o.concepto}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <Insignia tono={o.estado === "EJECUTADA" ? "exito" : "info"}>
          {ESTADO[o.estado] ?? o.estado}
        </Insignia>
        <span className="tabular-nums font-medium">
          A pagar {monto(o.monto_neto)} {o.divisa}
        </span>
      </div>

      {o.pagos.map((p) => (
        <section key={`${p.referencia}-${p.fecha_valor}`} className="tarjeta mt-6">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <Landmark size={14} aria-hidden={true} />
            Pago del {comoFecha(p.fecha_valor)}
          </h2>
          <dl className="tabular-nums mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            <dt style={{ color: "var(--fg-muted)" }}>Pagado</dt>
            <dd className="font-medium">
              {monto(p.monto_equivalente)} {o.divisa}
            </dd>
            <dt style={{ color: "var(--fg-muted)" }}>Debitado</dt>
            <dd>
              {monto(p.monto_debitado)} {p.divisa_cuenta} de {p.banco}
            </dd>
            {p.tasa_aplicada !== null && (
              <>
                <dt style={{ color: "var(--fg-muted)" }}>Tasa</dt>
                <dd>
                  {tasa(p.tasa_aplicada)} {p.divisa_cuenta}/{o.divisa}
                </dd>
              </>
            )}
            {!esCero(p.comision) && (
              <>
                <dt style={{ color: "var(--fg-muted)" }}>Comisión</dt>
                <dd>
                  {monto(p.comision)} {p.divisa_cuenta}
                </dd>
              </>
            )}
            {!esCero(p.monto_igtf) && (
              <>
                <dt style={{ color: "var(--fg-muted)" }}>IGTF</dt>
                <dd>
                  {monto(p.monto_igtf)} {p.divisa_cuenta}
                </dd>
              </>
            )}
            <dt style={{ color: "var(--fg-muted)" }}>Referencia</dt>
            <dd>{p.referencia}</dd>
            {p.ejecutado_por && (
              <>
                <dt style={{ color: "var(--fg-muted)" }}>Registró</dt>
                <dd>{p.ejecutado_por}</dd>
              </>
            )}
          </dl>
        </section>
      ))}

      <section className="tarjeta mt-6">
        <h2 className="text-sm font-medium">Detalle de la orden</h2>
        <ul className="mt-2 space-y-1 text-sm">
          {o.lineas.map((l) => (
            <li key={`${l.descripcion}-${l.monto}`} className="flex gap-3">
              <span className="min-w-0 flex-1">{l.descripcion}</span>
              <span className="tabular-nums">{monto(l.monto)}</span>
            </li>
          ))}
        </ul>
        <dl
          className="tabular-nums mt-3 grid grid-cols-[1fr_max-content] gap-x-4 gap-y-1 border-t pt-2 text-sm"
          style={{ borderColor: "var(--border)" }}
        >
          {desglose.map(([rotulo, valor]) => (
            <div key={rotulo} className="contents">
              <dt style={{ color: "var(--fg-muted)" }}>{rotulo}</dt>
              <dd className="text-right">
                {monto(valor)} {o.divisa}
              </dd>
            </div>
          ))}
          <dt className="font-medium">A pagar</dt>
          <dd className="text-right font-medium">
            {monto(o.monto_neto)} {o.divisa}
          </dd>
        </dl>
        <p className="mt-3 text-xs" style={{ color: "var(--fg-muted)" }}>
          {o.numero_documento ? `Factura ${o.numero_documento}` : "Sin número de factura"}
          {o.fecha_emision ? ` · emitida el ${comoFecha(o.fecha_emision)}` : ""}
          {o.redactada_por ? ` · redactó ${o.redactada_por}` : ""}
          {o.aprobada_por
            ? ` · aprobó ${o.aprobada_por}${o.aprobada_en ? ` el ${comoFechaHora(o.aprobada_en)}` : ""}`
            : ""}
        </p>
      </section>

      <section className="tarjeta mt-6">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <FileText size={14} aria-hidden={true} />
          {o.es_pago ? "Comprobante del pago" : "Soportes de la orden"}
        </h2>
        {o.soportes.length === 0 ? (
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            No hay archivos adjuntos.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {o.soportes.map((s) => (
              <li key={s.numero}>
                {/* La MISMA ruta pública, con el MISMO token: sin sesión y sin un id
                    que cambiar para llegar a otro archivo. */}
                <a
                  href={`/api/v1/publico/ordenes/${token}/soportes/${s.numero}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {s.nombre}
                </a>
                <span className="ml-2 text-xs" style={{ color: "var(--fg-muted)" }}>
                  {s.tipo === "COMPROBANTE" ? "comprobante" : s.tipo.toLowerCase()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
